import { useEffect, useState } from 'react'

import {
  TailgateGridContext,
  useTailgateGrid,
} from '../../hooks/useTailgateGrid'
import { useUpdateTailgateGrid } from '../../hooks/useUpdateTailgateGrid'
import { useGridAnalysisJob } from '../hooks/useGridAnalysisJob'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { HiOutlineBeaker } from 'react-icons/hi'

import {
  Flex,
  Icon,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'

import { GridAnalysisJobStatus } from '@/graphql/generated/schemas'

import {
  BoxType,
  CornerPoint,
  IncidentOptionI,
  TailgateBox,
  TailgateGridClick,
} from '../../types/types'
import {
  EMPTY_BOX,
  N_CORNERS,
  getBoxInput,
  isValidBox,
} from '../../utils/utils'
import { ExitGridTestingConfirmDialog } from './ExitGridTestingConfirmDialog'
import { GridEditingStep } from './GridEditingStep'
import { IncidentSelectExpectedCountsStep } from './IncidentSelectExpectedCountsStep'
import { InstructionsStep } from './InstructionsStep'
import { ProcessingStep } from './ProcessingStep'
import { ResultsStep } from './ResultsStep'
import { SaveGridConfirmDialog } from './SaveGridConfirmDialog'

interface GridTestingZoneIProps {
  isOpen: boolean
  onClose: () => void
  /**
   * When provided (e.g. launched from the Incident Drawer), the count-entry
   * step defaults to a "preset" mode that displays the pre-populated incident
   * on the left panel. The user can toggle to the search table and back.
   */
  presetIncident?: IncidentOptionI | null
}

export const GridTestingZone = ({
  isOpen,
  onClose,
  presetIncident,
}: GridTestingZoneIProps) => {
  const outerContext = useTailgateGrid()
  const { isImplementer } = usePermissions()
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [testInBox, setTestInBox] = useState<TailgateBox | null>(null)
  const [testOutBox, setTestOutBox] = useState<TailgateBox | null>(null)
  const [peopleIn, setPeopleIn] = useState(0)
  const [peopleOut, setPeopleOut] = useState(0)
  const [selectedIncident, setSelectedIncident] =
    useState<IncidentOptionI | null>(null)
  const [isExitConfirmOpen, setShowExitConfirm] = useState(false)
  const [isSaveConfirmOpen, setShowSaveConfirm] = useState(false)

  const { start, reset, job, status, isTimeout } = useGridAnalysisJob()
  const { showError } = useToasts()

  const { isUpdateLoading, updateGrid } = useUpdateTailgateGrid({
    associatedDevices: outerContext.associateDeviceValues,
    // Close BOTH the testing zone AND the outer TailgateGridModal on save.
    // Saving in the results step means "done" — the user expects to return
    // to the main UI, not to the tailgate grid modal.
    onClose: () => {
      resetAndClose()
      outerContext.onTailgateGridClose?.()
    },
  })

  // Snapshot state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTestInBox({
        ...outerContext.inBox,
        corners: outerContext.inBox?.corners?.map((c) => ({ ...c })) ?? [],
      })
      setTestOutBox({
        ...outerContext.outBox,
        corners: outerContext.outBox?.corners?.map((c) => ({ ...c })) ?? [],
      })
    }
  }, [isOpen, outerContext.inBox, outerContext.outBox])

  const resetAndClose = () => {
    reset()
    setStep(1)
    setTestInBox(null)
    setTestOutBox(null)
    setPeopleIn(0)
    setPeopleOut(0)
    setSelectedIncident(null)
    setShowExitConfirm(false)
    onClose()
  }

  const handleClose = () => {
    if (step === 1) {
      resetAndClose()
    } else {
      setShowExitConfirm(true)
    }
  }

  const isTestGridValid =
    testInBox?.isValid &&
    testOutBox?.isValid &&
    testInBox?.corners?.length === N_CORNERS &&
    testOutBox?.corners?.length === N_CORNERS

  const handleRunTest = async () => {
    if (!selectedIncident || !isTestGridValid) return
    setStep(4)
    outerContext.trackTailgateEvent?.('grid_test_job_started')
    await start({
      incidentId: selectedIncident.id,
      labeledIn: peopleIn,
      labeledOut: peopleOut,
      inBox: getBoxInput(testInBox),
      outBox: getBoxInput(testOutBox),
    })
  }

  // Fire completion/failure analytics on terminal status
  useEffect(() => {
    if (status === GridAnalysisJobStatus.Completed && job) {
      outerContext.trackTailgateEvent?.('grid_test_job_completed')
    }
    if (status === GridAnalysisJobStatus.Failed) {
      outerContext.trackTailgateEvent?.('grid_test_job_failed')
      showError(
        isTimeout
          ? 'The operation timed out. Please try again later.'
          : 'Grid analysis job failed. Please try again.'
      )
    }
  }, [status, job, outerContext, showError, isTimeout])

  const handleSelectAnotherIncident = () => {
    reset()
    outerContext.trackTailgateEvent?.('grid_test_select_another_incident')
    setStep(3)
    setSelectedIncident(null)
    setPeopleIn(0)
    setPeopleOut(0)
  }

  const handleRetry = () => {
    void handleRunTest()
  }

  const handleSaveGrid = () => {
    if (!testInBox || !testOutBox) return
    setShowSaveConfirm(true)
  }

  const handleConfirmSave = () => {
    if (!testInBox || !testOutBox) return
    outerContext.trackTailgateEvent?.('grid_test_save_grid')
    setShowSaveConfirm(false)
    updateGrid(testInBox, testOutBox, outerContext.videoDimensions)
  }

  const addTestBoxCorner = (
    point: { x: number; y: number },
    boxType: BoxType,
    setBox: (value: TailgateBox | ((prev: TailgateBox) => TailgateBox)) => void
  ) => {
    if (!outerContext.scaleX || !outerContext.scaleY) return
    setBox((b) => {
      const newCorner: CornerPoint = {
        x: point.x,
        y: point.y,
        scaledX: outerContext.scaleX(point.x),
        scaledY: outerContext.scaleY(point.y),
        id: b.corners.length + 1,
        type: boxType,
      }
      const updated = { ...b, corners: [...b.corners, newCorner] }
      if (updated.corners.length === N_CORNERS) {
        return { ...updated, isValid: isValidBox(updated) }
      }
      return updated
    })
  }

  const handleTestClick = (e: TailgateGridClick) => {
    if (!testInBox || !testOutBox) return
    const rect = e.currentTarget.getBoundingClientRect()
    const position = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
    if (testInBox.corners.length < N_CORNERS) {
      addTestBoxCorner(position, BoxType.IN_BOX, setTestInBox)
    } else if (testOutBox.corners.length < N_CORNERS) {
      addTestBoxCorner(position, BoxType.OUT_BOX, setTestOutBox)
    }
  }

  // Build an isolated context that prevents mutations from leaking to the parent
  const testContextValue =
    testInBox && testOutBox
      ? {
          ...outerContext,
          inBox: testInBox,
          outBox: testOutBox,
          setInBox: setTestInBox,
          setOutBox: setTestOutBox,
          clearBoxes: () => {
            setTestInBox(EMPTY_BOX)
            setTestOutBox(EMPTY_BOX)
          },
          setInitialBoxes: () => {
            setTestInBox({
              ...outerContext.inBox,
              corners:
                outerContext.inBox?.corners?.map((c) => ({ ...c })) ?? [],
            })
            setTestOutBox({
              ...outerContext.outBox,
              corners:
                outerContext.outBox?.corners?.map((c) => ({ ...c })) ?? [],
            })
          },
          handleOnClick: handleTestClick,
          refreshSnapshot: outerContext.refreshSnapshot,
          resetVideoFrame: outerContext.resetVideoFrame,
        }
      : null

  if (!isOpen || !testContextValue || !isImplementer) return null

  const modalSize = step === 1 ? '2xl' : step === 4 ? '3xl' : '6xl'
  const isProcessing = step === 4 && status === GridAnalysisJobStatus.Pending

  const displayStep =
    step === 2 ? 1 : step === 3 ? 2 : isProcessing ? 3 : step === 4 ? 4 : null

  return (
    <TailgateGridContext.Provider value={testContextValue}>
      <Modal
        isCentered
        isOpen={isOpen}
        motionPreset='slideInBottom'
        onClose={handleClose}
        scrollBehavior={step === 2 ? 'inside' : 'outside'}
        size={modalSize}
      >
        <ModalOverlay />
        <ModalContent
          bg='#fff'
          border='2px solid'
          borderColor='purple.400'
          px={{ base: '5px', sm: '15px', md: '30px' }}
          py='30px'
        >
          {!isProcessing && (
            <ModalCloseButton color='#000' mt='5px' opacity={0.4} />
          )}

          {step > 1 && (
            <Flex
              align='center'
              bg='purple.50'
              border='1px solid'
              borderColor='purple.200'
              borderRadius='md'
              gap={2}
              justifyContent='space-between'
              mb={5}
              mr={5}
              mt={1}
              px={3}
              py={2}
            >
              <Flex align='center' gap={2}>
                <Icon as={HiOutlineBeaker} boxSize='18px' color='purple.600' />
                <Text color='purple.700' fontSize='sm' fontWeight='semibold'>
                  Grid Testing Zone
                </Text>
              </Flex>
              <Text color='purple.500' fontSize='xs' fontWeight='medium'>
                Step {displayStep} of 4
              </Text>
            </Flex>
          )}

          {step === 1 && (
            <InstructionsStep
              onBegin={() => setStep(2)}
              onClose={resetAndClose}
            />
          )}

          {step === 2 && (
            <GridEditingStep
              isNextDisabled={!isTestGridValid}
              onClose={handleClose}
              onNext={() => setStep(3)}
            />
          )}

          {step === 3 && (
            <IncidentSelectExpectedCountsStep
              isRunTestDisabled={!isTestGridValid}
              onBack={() => {
                setSelectedIncident(null)
                setPeopleIn(0)
                setPeopleOut(0)
                setStep(2)
              }}
              onClose={handleClose}
              onRunTest={handleRunTest}
              peopleIn={peopleIn}
              peopleOut={peopleOut}
              presetIncident={presetIncident}
              selectedIncident={selectedIncident}
              setPeopleIn={setPeopleIn}
              setPeopleOut={setPeopleOut}
              setSelectedIncident={setSelectedIncident}
            />
          )}

          {step === 4 && status === GridAnalysisJobStatus.Pending && (
            <ProcessingStep />
          )}

          {step === 4 && status === GridAnalysisJobStatus.Completed && job && (
            <ResultsStep
              isSaving={isUpdateLoading}
              job={job}
              labeledIn={peopleIn}
              labeledOut={peopleOut}
              onClose={handleClose}
              onSaveGrid={handleSaveGrid}
              onSelectAnotherIncident={handleSelectAnotherIncident}
              selectedIncident={selectedIncident}
              variant='success'
            />
          )}

          {step === 4 && status === GridAnalysisJobStatus.Failed && (
            <ResultsStep
              isTimeout={isTimeout}
              onClose={handleClose}
              onRetry={handleRetry}
              onSelectAnotherIncident={handleSelectAnotherIncident}
              selectedIncident={selectedIncident}
              variant='error'
            />
          )}
        </ModalContent>
      </Modal>
      <ExitGridTestingConfirmDialog
        isOpen={isExitConfirmOpen}
        onCancel={() => setShowExitConfirm(false)}
        onConfirm={resetAndClose}
      />
      <SaveGridConfirmDialog
        isOpen={isSaveConfirmOpen}
        isSaving={isUpdateLoading}
        onCancel={() => setShowSaveConfirm(false)}
        onConfirm={handleConfirmSave}
      />
    </TailgateGridContext.Provider>
  )
}
